import { ChangeDetectionStrategy, Component, Inject, Self } from '@angular/core';
import { ClientStageEnum } from '@libs/enums/client-stage.enum';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { LoanService } from '@libs/services/loan.service';
import { ClientStore } from '@libs/shared/storage/client.store';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import { ClientModel } from '@libs/models/client.model';
import { ICallbackData, IPath } from '@libs/models/models';
import * as Config from '@libs/configs/general-config';
import { SendEventHelper } from '@libs/helpers/send-event.helper';
import { ENVIRONMENT } from '@libs/shared/injections/window';
import { ToasterService } from '@libs/shared/components/toaster-widget/toaster.service';
import { EnvironmentType } from '@libs/models/environment.type';
import { NamesEnum } from '@libs/enums/names.enum';
import { UnsubscribeService } from '@libs/shared/services/unsubscribe.service';

@Component({
  selector: 'app-verification-callback',
  templateUrl: './verification-callback.component.html',
  styleUrls: ['./verification-callback.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [UnsubscribeService],
})
export class VerificationCallbackComponent {
  private path: IPath = Config.StaticDataConfig.redirectPathByClientStage;
  private systemStages: ClientStageEnum[] = [
    ClientStageEnum.CLIENT,
    ClientStageEnum.CARD,
    ClientStageEnum.APPLICATION,
    ClientStageEnum.LOAN,
    ClientStageEnum.FINISHED_LOAN,
  ];

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private loanService: LoanService,
    private clientStore: ClientStore,
    @Inject(ENVIRONMENT) private environment: EnvironmentType,
    private eventHelper: SendEventHelper,
    private toaster: ToasterService,
    @Self() private destroyStream$: UnsubscribeService,
  ) {
    this.eventHelper.send('call back bank id init');
    this.activatedRoute.queryParams
      .pipe(
        map((params: Params) => {
          if (!params || !params.code || !params.state) {
            this.eventHelper.send(`CallBackError`);
            this.router.navigate(['/']);
            throw new Error('No data found...');
          }
          this.eventHelper.send('CallBack with params');
          const { code, state } = params;
          return { code, state };
        }),
        switchMap((data: ICallbackData) => {
          const skipInterceptor = [NamesEnum.STARFIN, NamesEnum.SUNCREDIT].includes(this.environment.name as NamesEnum);
          return this.loanService.postBankIdCallback(data, skipInterceptor);
        }),
        takeUntil(this.destroyStream$),
      )
      .subscribe({
        next: (client: ClientModel) => this.handleSuccess(client),
        error: (error: any) => this.handleError(error),
        complete: () => {},
      });
  }

  private handleSuccess(client: ClientModel): void {
    this.eventHelper.send('post bankid/customer-info');
    this.clientStore.setClient(client);
    this.router.navigate(this.getRedirectUrl(client));
  }

  private handleError(error: any): void {
    if (this.environment.group !== NamesEnum.AVANS) {
      this.toaster.error('errors.BANK_RESPONSE_ERROR');
    }
    this.eventHelper.send('error in app-verification-callback');
    this.clientStore
      .getClient()
      .pipe(take(1))
      .subscribe((client: ClientModel) => {
        this.router.navigate(this.getRedirectUrl(client));
      });
  }

  private getRedirectUrl(client: ClientModel): string[] {
    return this.checkOldUser(client) ? ['main', 'loan'] : ['form', this.path.form[client.stage]];
  }

  private checkOldUser(client: ClientModel): boolean {
    return this.systemStages.findIndex((stage: ClientStageEnum) => stage === client.stage) !== -1;
  }
}
